import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import CFOCostMargin from "./CFOCostMargin";
import CFOCard from "./CFOCard";

// ─────────────────────────────────────────────────────────────────────────────
// HENRY-GRIFFITTS CFO  ·  "We Deliver Confidence"
// Ported from HG-CFO.jsx (standalone) into the Hub. Deliberate deviations only:
//   · the CFO system prompt is SERVER-SIDE (POST /api/cfo/ask) — never shipped here
//   · snapshots + rules/overrides persist via /api/cfo/* (the "brain" survives reloads)
//   · the demo dataset is FICTIONAL — real customer revenue never ships in the bundle
// Everything else (parsing, classification, verdicts, styling) is the original.
// ─────────────────────────────────────────────────────────────────────────────

const API = "/api";
const NAVY = "#102134", GREEN = "#48B64C", GOLD = "#966E40";
const PAPER = "#FAF8F4", INK = "#1A1A1A", MUTE = "#6B7280";
// White wordmark+tree for dark grounds — the same Cloudinary asset the email dark
// footers use (the old embedded base64 blob was corrupt and didn't render).
const HG_LOGO = "https://res.cloudinary.com/dflzi5xc3/image/upload/hg-brand/logo-white.png";

const DEFAULT_RULES = [
  { match: "HG Pacific", kind: "intercompany", note: "Australia subsidiary — declines = restock timing, not lost demand." },
  { match: "HGL", kind: "house", note: "Internal / house account — not retail fitting demand." },
  { match: "JumboMax", kind: "oneoff", note: "One-time project partner, not a club customer. Won't recur." },
  { match: "Operation 36", kind: "newchannel", note: "Op36 — the new growth engine. Gains here are strategic." },
  { match: "Op36", kind: "newchannel", note: "Op36 channel." },
  { match: "Avoda", kind: "newchannel", note: "Growing distributor account." },
];

const KIND_META = {
  intercompany: { label: "Intercompany", color: "#7C6F9C", real: false, blurb: "Inventory timing — not lost demand." },
  oneoff:       { label: "One-time project", color: "#B08968", real: false, blurb: "Non-recurring — shouldn't count against the year." },
  temporary:    { label: "Temporary", color: "#C99B3A", real: false, blurb: "Recoverable (leave, pregnancy, seasonal)." },
  house:        { label: "House account", color: "#888", real: false, blurb: "Internal — not retail demand." },
  newchannel:   { label: "New channel", color: GREEN, real: false, blurb: "Strategic growth — Op36 / distributors." },
  fitter:       { label: "Fitter (real)", color: "#C0392B", real: true, blurb: "Real, addressable. Call them." },
  wholesale:    { label: "Wholesale/B2B", color: "#5A7D9A", real: true, blurb: "Component/wholesale — worth chasing." },
  unclassified: { label: "Unclassified", color: "#555", real: true, blurb: "No rule yet — treated as real until tagged." },
};

const fmt = (n) => (n < 0 ? "-$" : "$") + Math.abs(Math.round(n)).toLocaleString();

function parseCSV(text) {
  const rows = []; let row = [], cell = "", q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) { if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; } else if (c === '"') q = false; else cell += c; }
    else {
      if (c === '"') q = true;
      else if (c === ",") { row.push(cell); cell = ""; }
      else if (c === "\n" || c === "\r") { if (c === "\r" && text[i + 1] === "\n") i++; row.push(cell); rows.push(row); row = []; cell = ""; }
      else cell += c;
    }
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter((r) => r.some((x) => x.trim() !== ""));
}
const num = (s) => { if (s == null) return NaN; let t = String(s).trim().replace(/[$,]/g, ""); if (/^\(.*\)$/.test(t)) t = "-" + t.slice(1, -1); const v = parseFloat(t); return isNaN(v) ? NaN : v; };

function parseCustomerSummary(rows) {
  const out = [];
  for (const r of rows) {
    const name = (r[0] || "").trim();
    if (!name || /^total|sales by customer|henry-griffitts|profit|accrual/i.test(name)) continue;
    const nums = r.slice(1).map(num).filter((x) => !isNaN(x));
    if (nums.length === 0) continue;
    const cur = nums[0] ?? 0, pri = nums.length > 1 ? nums[1] : 0;
    if (cur === 0 && pri === 0) continue;
    out.push({ name, cur, pri, delta: cur - pri });
  }
  return out;
}
function classify(name, rules) {
  for (const rule of rules) if (name.toLowerCase().includes(rule.match.toLowerCase())) return { kind: rule.kind, note: rule.note };
  return { kind: "unclassified", note: "" };
}

export default function HGCFO({ showCard = false }) {
  const [customers, setCustomers] = useState(null);
  const [rules, setRules] = useState(DEFAULT_RULES);
  const [overrides, setOverrides] = useState({});
  const [cash, setCash] = useState({ balance: "", monthlyBurn: "" });
  const [showRules, setShowRules] = useState(false);
  const [fileName, setFileName] = useState("");
  const [pastSnapshots, setPastSnapshots] = useState([]);
  const [hydrated, setHydrated] = useState(false);
  // Which CFO module is shown: "analysis" (the original CFO), "costs" (Component
  // Cost & Margin) or "card" (Card & Subscriptions — only while CFO_CARD_LIVE is on,
  // which /api/features reports). All stay mounted so state survives switching.
  const [cfoModule, setCfoModule] = useState("analysis");
  const fileRef = useRef();

  // ---- persistence: the "brain" (rules + overrides) and monthly snapshots ----
  useEffect(() => {
    (async () => {
      try {
        const [r, s] = await Promise.all([
          axios.get(`${API}/cfo/rules`), axios.get(`${API}/cfo/snapshots`),
        ]);
        if (Array.isArray(r.data.rules) && r.data.rules.length) setRules(r.data.rules);
        if (r.data.overrides) setOverrides(r.data.overrides);
        setPastSnapshots(s.data.snapshots || []);
      } catch { /* first run / not allowlisted — defaults are fine */ }
      setHydrated(true);
    })();
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const t = setTimeout(() => {
      axios.post(`${API}/cfo/rules`, { rules, overrides }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [rules, overrides, hydrated]);

  const saveSnapshot = useCallback((accounts, name) => {
    const month = new Date().toISOString().slice(0, 7);
    const totals = {
      cur: accounts.reduce((s, c) => s + c.cur, 0),
      pri: accounts.reduce((s, c) => s + c.pri, 0),
    };
    axios.post(`${API}/cfo/snapshots`, { month, file_name: name, accounts, totals })
      .then(() => axios.get(`${API}/cfo/snapshots`))
      .then((res) => setPastSnapshots(res.data.snapshots || []))
      .catch(() => {});
  }, []);

  function handleFile(e) {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCustomerSummary(parseCSV(reader.result));
      setCustomers(parsed);
      saveSnapshot(parsed, f.name);
    };
    reader.readAsText(f);
  }
  function loadSnapshot(month) {
    const snap = pastSnapshots.find((s) => s.month === month);
    if (!snap) return;
    setCustomers(snap.accounts);
    setFileName(`${snap.file_name || "Saved snapshot"} (${snap.month})`);
  }
  function loadSample() {
    // FICTIONAL demo data only — real customer revenue never ships in this bundle.
    const sample = [
      ["Sample Intl Subsidiary (HG Pacific)","-3000.00","33000.00"],["Sample Project (JumboMax)","100.00","31000.00"],
      ["HGL - House sale","28000.00","50000.00"],["Sample Fitter A","25000.00","41000.00"],
      ["Sample Fitter B","500.00","10000.00"],["Sample Course C","0","8300.00"],
      ["Sample Program D","2100.00","9400.00"],["Sample Wholesale E","2600.00","9800.00"],
      ["Sample Fitter F","5400.00","11800.00"],["Sample Academy G","450.00","6100.00"],
      ["Sample Fitter H","2100.00","6800.00"],["Sample Club I","300.00","4400.00"],
      ["Operation 36","23400.00","0"],["Avoda Golf USA","24200.00","7800.00"],
      ["Sample Course J","8500.00","2000.00"],["Sample Fitter K","7400.00","2100.00"],
      ["Sample Fitter L","9500.00","5500.00"],["Sample Fitter M","17600.00","14100.00"],
      ["Sample Fitter N","8300.00","11600.00"],["Sample Fitter O","4200.00","0"],
    ];
    setCustomers(parseCustomerSummary(sample));
    setFileName("Fictional demo data");
  }

  const analysis = useMemo(() => {
    if (!customers) return null;
    const tagged = customers.map((c) => {
      const kind = overrides[c.name] || classify(c.name, rules).kind;
      return { ...c, kind, note: classify(c.name, rules).note, real: KIND_META[kind].real };
    });
    const totalCur = tagged.reduce((s, c) => s + c.cur, 0);
    const totalPri = tagged.reduce((s, c) => s + c.pri, 0);
    const headlineDelta = totalCur - totalPri;
    const realDelta = tagged.filter((c) => c.real).reduce((s, c) => s + c.delta, 0);
    const noiseDelta = headlineDelta - realDelta;
    const declines = tagged.filter((c) => c.delta < 0).sort((a, b) => a.delta - b.delta);
    const gains = tagged.filter((c) => c.delta > 0).sort((a, b) => b.delta - a.delta);
    const realDeclines = declines.filter((c) => c.real);
    return { tagged, totalCur, totalPri, headlineDelta, realDelta, noiseDelta, declines, gains, realDeclines };
  }, [customers, rules, overrides]);

  const runway = useMemo(() => {
    const bal = num(cash.balance), burn = num(cash.monthlyBurn);
    if (isNaN(bal) || isNaN(burn) || burn <= 0) return null;
    return bal / burn;
  }, [cash]);

  return (
    <div style={{ minHeight: "100vh", background: PAPER, color: INK, fontFamily: "'Inter', system-ui, sans-serif", margin: "-1rem -1rem 0", borderRadius: 8, overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,500;9..144,600&display=swap');
        .cfo-root *{box-sizing:border-box;}
        .fr{font-family:'Fraunces',Georgia,serif;}
        .card{background:#fff;border:1px solid #ECE7DE;border-radius:14px;}
        .btn{cursor:pointer;border:none;border-radius:9px;font-weight:600;font-family:inherit;transition:transform .08s;}
        .btn:active{transform:translateY(1px);}
        .chip{display:inline-block;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:600;color:#fff;}
        .row:hover{background:#FBFAF7;}
        .cfo-root select{font-family:inherit;font-size:12px;padding:3px 6px;border-radius:7px;border:1px solid #D9D3C8;background:#fff;cursor:pointer;color:#1A1A1A;}
        @keyframes blink{0%,100%{opacity:.3}50%{opacity:1}}
        .dot{animation:blink 1.2s infinite;}
      `}</style>

      <div className="cfo-root">
      <div style={{ background: NAVY, color: "#fff", padding: "24px 28px 22px", borderBottom: `3px solid ${GREEN}` }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "flex", alignItems: "center", gap: 18 }}>
          <img src={HG_LOGO} alt="Henry-Griffitts" style={{ height: 46, width: "auto", display: "block" }} />
          <div style={{ borderLeft: "1px solid #2A3A4D", paddingLeft: 18 }}>
            <div style={{ fontSize: 10.5, letterSpacing: 4, color: GOLD, fontWeight: 700, textTransform: "uppercase" }}>Est. 1983</div>
            <div className="fr" style={{ fontSize: 32, fontWeight: 600, lineHeight: 1.05, marginTop: 2 }}>The CFO<span style={{ color: GREEN }}>.</span></div>
          </div>
          <div style={{ marginLeft: "auto", textAlign: "right", color: "#9FB0BE", fontSize: 12, fontStyle: "italic" }} className="fr">We Deliver Confidence</div>
        </div>
      </div>

      {/* CFO module switch — Analysis | Cost & Margin | Card & Subscriptions */}
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "18px 20px 0", display: "flex", gap: 8 }}>
        {[["analysis", "CFO Analysis"], ["costs", "Cost & Margin"], ...(showCard ? [["card", "Card & Subscriptions"]] : [])].map(([k, lbl]) => (
          <button key={k} className="btn" onClick={() => setCfoModule(k)}
            style={{ padding: "8px 16px", fontSize: 13, background: cfoModule === k ? NAVY : "#fff", color: cfoModule === k ? "#fff" : NAVY, border: `1px solid ${cfoModule === k ? NAVY : "#D9D3C8"}` }}>
            {lbl}
          </button>
        ))}
      </div>

      <div style={{ display: cfoModule === "costs" ? "block" : "none" }}>
        <CFOCostMargin />
      </div>

      {showCard && (
        <div style={{ display: cfoModule === "card" ? "block" : "none" }}>
          <CFOCard />
        </div>
      )}

      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "24px 20px 80px", display: cfoModule === "analysis" ? "block" : "none" }}>
        {!analysis && (
          <div className="card" style={{ padding: 34, textAlign: "center" }}>
            <div className="fr" style={{ fontSize: 22, fontWeight: 600, color: NAVY }}>Start with your Sales by Customer Summary</div>
            <div style={{ color: MUTE, fontSize: 14, margin: "8px auto 22px", maxWidth: 540 }}>
              In QuickBooks: <b>Reports → Sales by Customer Summary</b>, set the period with a prior-year comparison, then <b>Export → CSV</b>. Drop it here. The CFO already knows HG's accounts — HG Pacific is a restock, JumboMax was a one-off, Op36 is the new channel.
            </div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{ display: "none" }} />
            <button className="btn" onClick={() => fileRef.current.click()} style={{ background: GREEN, color: "#fff", padding: "13px 26px", fontSize: 15 }}>Choose CSV file</button>
            {pastSnapshots.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <span style={{ fontSize: 13, color: MUTE, marginRight: 8 }}>or reopen a saved month:</span>
                <select defaultValue="" onChange={(e) => e.target.value && loadSnapshot(e.target.value)}>
                  <option value="" disabled>Pick a month…</option>
                  {pastSnapshots.map((s) => <option key={s.month} value={s.month}>{s.month} — {s.file_name || "snapshot"}</option>)}
                </select>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <button className="btn" onClick={loadSample} style={{ background: "transparent", color: GOLD, padding: "6px 10px", fontSize: 13, textDecoration: "underline" }}>or load fictional demo data</button>
            </div>
          </div>
        )}

        {analysis && (
          <>
            <div className="card" style={{ padding: "22px 26px", marginBottom: 18 }}>
              <div style={{ fontSize: 11, letterSpacing: 2, color: GOLD, fontWeight: 700, textTransform: "uppercase" }}>{fileName}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 26, marginTop: 14, alignItems: "flex-end" }}>
                <Stat label="Headline change" value={fmt(analysis.headlineDelta)} color={analysis.headlineDelta < 0 ? "#C0392B" : GREEN} big sub={`${fmt(analysis.totalPri)} → ${fmt(analysis.totalCur)}`} />
                <div className="fr" style={{ fontSize: 28, color: "#D9D3C8", paddingBottom: 16 }}>but</div>
                <Stat label="Real, addressable change" value={fmt(analysis.realDelta)} color={analysis.realDelta < 0 ? "#C0392B" : GREEN} big sub="after timing, one-offs & temporary" />
                <Stat label="Noise (not real demand)" value={fmt(analysis.noiseDelta)} color={MUTE} sub="intercompany / projects / house / new channel" />
              </div>
              <div style={{ marginTop: 16, padding: "12px 15px", background: "#F3F7F3", borderRadius: 10, fontSize: 14, lineHeight: 1.5, borderLeft: `3px solid ${GREEN}` }}>{cfoVerdict(analysis)}</div>
            </div>

            <div className="card" style={{ padding: "20px 26px", marginBottom: 18 }}>
              <SectionTitle>Do this week</SectionTitle>
              {analysis.realDeclines.length === 0 && <div style={{ color: MUTE, fontSize: 14 }}>No real declines flagged — the drop is timing, projects, or new-channel transition. Nothing to chase.</div>}
              {analysis.realDeclines.slice(0, 5).map((c, i) => (
                <div key={c.name} style={{ display: "flex", alignItems: "baseline", gap: 12, padding: "9px 0", borderBottom: i < Math.min(4, analysis.realDeclines.length - 1) ? "1px solid #F0EBE2" : "none" }}>
                  <div className="fr" style={{ fontSize: 20, fontWeight: 700, color: GOLD, width: 26 }}>{i + 1}</div>
                  <div style={{ flex: 1 }}><b>Call {c.name}.</b> Down {fmt(Math.abs(c.delta))} ({fmt(c.pri)} → {fmt(c.cur)}). <span style={{ color: MUTE }}>{KIND_META[c.kind].blurb}</span></div>
                  <span className="chip" style={{ background: KIND_META[c.kind].color }}>{KIND_META[c.kind].label}</span>
                </div>
              ))}
            </div>

            <div className="card" style={{ padding: "20px 26px", marginBottom: 18 }}>
              <SectionTitle>Cash runway</SectionTitle>
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
                <Field label="Cash on hand ($)" value={cash.balance} onChange={(v) => setCash({ ...cash, balance: v })} placeholder="92000" />
                <Field label="Net monthly burn ($)" value={cash.monthlyBurn} onChange={(v) => setCash({ ...cash, monthlyBurn: v })} placeholder="8000" />
                {runway != null && (
                  <div style={{ paddingBottom: 2 }}>
                    <div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1 }}>Runway</div>
                    <div className="fr" style={{ fontSize: 30, fontWeight: 600, color: runway < 4 ? "#C0392B" : runway < 8 ? GOLD : GREEN }}>{runway.toFixed(1)} mo</div>
                  </div>
                )}
              </div>
              {runway != null && <div style={{ marginTop: 12, fontSize: 13, color: MUTE, lineHeight: 1.5 }}>{runway < 4 ? "Tight. This is the bridge-cash window — line up working capital before the channel experiments mature, so you're not forced into a bad raise under pressure." : runway < 8 ? "Workable but watch it. Enough room to let the 90-day cohort prove out; keep the bridge conversation warm." : "Comfortable. Room to let the channels prove out before sizing any raise."}</div>}
              {runway == null && <div style={{ fontSize: 13, color: MUTE, marginTop: 6 }}>Enter cash and monthly burn to see your runway.</div>}
            </div>

            <div className="card" style={{ padding: "20px 26px", marginBottom: 18 }}>
              <SectionTitle>Where the change came from</SectionTitle>
              <div style={{ fontSize: 12, color: MUTE, marginBottom: 10 }}>Tag any account to change how it counts. Real buckets count against you; the rest is noise.</div>
              {analysis.declines.slice(0, 12).map((c) => <LineItem key={c.name} c={c} overrides={overrides} setOverrides={setOverrides} />)}
            </div>

            <div className="card" style={{ padding: "20px 26px", marginBottom: 18 }}>
              <SectionTitle>What's growing</SectionTitle>
              {analysis.gains.slice(0, 8).map((c) => <LineItem key={c.name} c={c} overrides={overrides} setOverrides={setOverrides} />)}
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button className="btn" onClick={() => { setCustomers(null); setFileName(""); }} style={{ background: NAVY, color: "#fff", padding: "11px 20px" }}>Load a new report</button>
              <button className="btn" onClick={() => setShowRules(!showRules)} style={{ background: "#fff", color: NAVY, border: "1px solid #D9D3C8", padding: "11px 20px" }}>{showRules ? "Hide" : "Edit"} classification rules</button>
            </div>
            {showRules && <RulesEditor rules={rules} setRules={setRules} />}
          </>
        )}

        <AskCFO analysis={analysis} cash={cash} runway={runway} />
      </div>
      </div>
    </div>
  );
}

function cfoVerdict(a) {
  const head = a.headlineDelta, real = a.realDelta;
  if (head < 0 && real >= head * 0.5) return `The headline says you're ${fmt(Math.abs(head))} down — but ${fmt(Math.abs(a.noiseDelta))} of that is timing, one-time projects, or new-channel transition, not lost demand. Real, addressable change is ${fmt(real)}. ${a.realDeclines[0] ? `The account worth your attention: ${a.realDeclines[0].name}.` : "Nothing real to chase."}`;
  if (head >= 0) return `Up ${fmt(head)} on the period. ${a.realDeclines[0] ? `Still worth a look: ${a.realDeclines[0].name} is down ${fmt(Math.abs(a.realDeclines[0].delta))}.` : "No real declines flagged."}`;
  return `Real addressable change is ${fmt(real)}. Focus there, not the headline.`;
}

function AskCFO({ analysis, cash, runway }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef();

  function snapshot() {
    if (!analysis) return "No report loaded yet.";
    const top = analysis.declines.slice(0, 6).map((c) => `${c.name}: ${fmt(c.pri)}→${fmt(c.cur)} (${fmt(c.delta)}, ${KIND_META[c.kind].label})`).join("; ");
    const grow = analysis.gains.slice(0, 4).map((c) => `${c.name} +${fmt(c.delta)}`).join("; ");
    return `Current report — Headline change ${fmt(analysis.headlineDelta)}, Real addressable ${fmt(analysis.realDelta)}, Noise ${fmt(analysis.noiseDelta)}. Top declines: ${top}. Growing: ${grow}. Cash ${cash.balance || "n/a"}, burn ${cash.monthlyBurn || "n/a"}, runway ${runway != null ? runway.toFixed(1) + " mo" : "n/a"}.`;
  }

  async function send() {
    const q = input.trim(); if (!q || busy) return;
    const next = [...msgs, { role: "user", content: q }];
    setMsgs(next); setInput(""); setBusy(true);
    try {
      // Server-side proxy: the system prompt + Anthropic key never reach this browser.
      const res = await axios.post(`${API}/cfo/ask`, {
        messages: next.map((m) => ({ role: m.role, content: m.content })),
        snapshot: snapshot(),
      });
      setMsgs([...next, { role: "assistant", content: res.data.text || "No response." }]);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setMsgs([...next, { role: "assistant", content: detail || "Couldn't reach the model. Try again in a moment." }]);
    }
    setBusy(false);
    setTimeout(() => boxRef.current?.scrollTo(0, boxRef.current.scrollHeight), 50);
  }

  const starters = ["Why am I really down this year?", "Should I raise money now?", "What should I do this week?", "Does my software add value to HG?"];

  return (
    <div className="card" style={{ padding: "20px 26px", marginTop: 18, borderTop: `3px solid ${GOLD}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: GOLD }} />
        <SectionTitle>Ask the CFO</SectionTitle>
      </div>
      <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 12 }}>A live advisor with HG's full business context loaded server-side. It sees whatever report you've loaded.</div>

      <div ref={boxRef} style={{ maxHeight: 320, overflowY: "auto", display: msgs.length ? "block" : "none", marginBottom: 12 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ margin: "8px 0", display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "82%", padding: "10px 14px", borderRadius: 12, fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", background: m.role === "user" ? NAVY : "#F3F1EC", color: m.role === "user" ? "#fff" : INK, border: m.role === "assistant" ? "1px solid #E7E2D8" : "none" }}>{m.content}</div>
          </div>
        ))}
        {busy && <div style={{ fontSize: 13, color: MUTE, padding: "6px 4px" }}><span className="dot">●</span> thinking…</div>}
      </div>

      {!msgs.length && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {starters.map((s) => <button key={s} className="btn" onClick={() => setInput(s)} style={{ background: "#F3F1EC", color: NAVY, padding: "7px 12px", fontSize: 12.5, fontWeight: 500, border: "1px solid #E7E2D8" }}>{s}</button>)}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask about your numbers, the raise, Op36…"
          style={{ flex: 1, padding: "11px 14px", borderRadius: 10, border: "1px solid #D9D3C8", fontSize: 14, fontFamily: "inherit", color: INK, background: "#fff" }} />
        <button className="btn" onClick={send} disabled={busy} style={{ background: busy ? "#9BBF9D" : GREEN, color: "#fff", padding: "11px 22px", fontSize: 14 }}>Ask</button>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, color, big }) {
  return (<div><div style={{ fontSize: 11, color: "#8A8276", textTransform: "uppercase", letterSpacing: 1, fontWeight: 600 }}>{label}</div>
    <div className="fr" style={{ fontSize: big ? 34 : 24, fontWeight: 600, color, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: MUTE, marginTop: 2 }}>{sub}</div>}</div>);
}
function SectionTitle({ children }) { return <div className="fr" style={{ fontSize: 19, fontWeight: 600, marginBottom: 12, color: NAVY }}>{children}</div>; }
function Field({ label, value, onChange, placeholder }) {
  return (<div><div style={{ fontSize: 11, color: MUTE, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
    <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      style={{ padding: "9px 12px", borderRadius: 9, border: "1px solid #D9D3C8", fontSize: 15, width: 150, fontFamily: "inherit", color: INK, background: "#fff" }} /></div>);
}
function LineItem({ c, overrides, setOverrides }) {
  return (<div className="row" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 6px", borderBottom: "1px solid #F4F0E8", fontSize: 14 }}>
    <div style={{ flex: 1, minWidth: 0 }}><span style={{ fontWeight: 600 }}>{c.name}</span>{c.note && <div style={{ fontSize: 11.5, color: MUTE, marginTop: 1 }}>{c.note}</div>}</div>
    <div style={{ width: 92, textAlign: "right", color: MUTE, fontSize: 13 }}>{fmt(c.pri)}→{fmt(c.cur)}</div>
    <div style={{ width: 80, textAlign: "right", fontWeight: 700, color: c.delta < 0 ? "#C0392B" : GREEN }}>{c.delta < 0 ? "" : "+"}{fmt(c.delta)}</div>
    <select value={c.kind} onChange={(e) => setOverrides({ ...overrides, [c.name]: e.target.value })} style={{ width: 130 }}>
      {Object.entries(KIND_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
    </select>
  </div>);
}
function RulesEditor({ rules, setRules }) {
  const [match, setMatch] = useState(""), [kind, setKind] = useState("fitter");
  return (<div className="card" style={{ padding: 20, marginTop: 16 }}>
    <SectionTitle>Classification rules</SectionTitle>
    <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 12 }}>These teach the CFO your business. A customer name containing the text gets tagged automatically — what turns "way down, panic" into "small real dip, make one call."</div>
    {rules.map((r, i) => (<div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #F4F0E8", fontSize: 13 }}>
      <code style={{ background: "#F3EFE7", padding: "2px 7px", borderRadius: 5, minWidth: 130 }}>{r.match}</code>
      <span className="chip" style={{ background: KIND_META[r.kind].color }}>{KIND_META[r.kind].label}</span>
      <span style={{ flex: 1, color: MUTE, fontSize: 12 }}>{r.note}</span>
      <button className="btn" onClick={() => setRules(rules.filter((_, j) => j !== i))} style={{ background: "transparent", color: "#C0392B", fontSize: 18, padding: "0 6px" }}>×</button>
    </div>))}
    <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
      <input type="text" value={match} onChange={(e) => setMatch(e.target.value)} placeholder="Name contains…" style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid #D9D3C8", fontSize: 13, width: 160, fontFamily: "inherit", color: INK, background: "#fff" }} />
      <select value={kind} onChange={(e) => setKind(e.target.value)}>{Object.entries(KIND_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}</select>
      <button className="btn" onClick={() => { if (match.trim()) { setRules([...rules, { match: match.trim(), kind, note: "" }]); setMatch(""); } }} style={{ background: GREEN, color: "#fff", padding: "8px 16px", fontSize: 13 }}>Add rule</button>
    </div>
  </div>);
}
